import React, { useState, useCallback, useEffect } from 'react';
import { Text } from '@shootsta/common-react';
import { useVideoShareSettings } from './hooks/useVideoShareSettings';
import { useWorkspaceSharing } from './hooks/useWorkspaceSharing';
import ShareStatusToggle from './components/ShareStatusToggle';
import AccessLevelSelector from './components/AccessLevelSelector';
import PublicLinkSection from './components/PublicLinkSection';
import WorkspaceLinkSection from './components/WorkspaceLinkSection';
import WorkspaceAccessSection from './components/WorkspaceAccessSection';
import './styles/_video-share-modal.scss';

type VideoShareModalProps = {
  videoId: string;
  visible: boolean;
  onClose: () => void;
  apiClient: any;
  organisationId?: string;
  shareUrl?: string;
  canEdit?: boolean;
};

const TABS = ['Public', 'Workspace'] as const;
type Tab = (typeof TABS)[number];

function VideoShareModal({
  videoId,
  visible,
  onClose,
  apiClient,
  organisationId = '',
  shareUrl,
  canEdit: canEditProp
}: VideoShareModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Public');

  const { settings, loading, updateSettings, refetch, canEdit: canEditResolved } =
    useVideoShareSettings(apiClient, videoId);

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  const canEdit = canEditProp ?? canEditResolved;

  const effectiveOrgId = organisationId || settings?.organisationId || '';

  const {
    roles,
    workspaceSettings,
    loading: workspaceLoading,
    updateWorkspaceSettings
  } = useWorkspaceSharing(apiClient, videoId, effectiveOrgId);

  const workspaceRoleName = workspaceSettings?.role?.name;

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!visible) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div className="video-share-modal__backdrop" onClick={handleBackdropClick}>
      <div className="video-share-modal">
        <div className="video-share-modal__header">
          <Text heading3 noMargin>
            Share Video
          </Text>
          <button
            type="button"
            className="video-share-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="video-share-modal__body">
          {loading ? (
            <div className="video-share-modal__loading">
              <Text>Loading share settings...</Text>
            </div>
          ) : (
            <>
              <div className="video-share-modal__tabs">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    type="button"
                    className={`video-share-modal__tab ${activeTab === tab ? 'video-share-modal__tab--active' : ''}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              <div className="video-share-modal__tab-content">
                {!canEdit && (
                  <div className="video-share-modal__read-only-notice">
                    <Text noMargin>
                      You can view share settings and copy links, but don't have
                      permission to change them.
                    </Text>
                  </div>
                )}

                {activeTab === 'Public' && (
                  <>
                    <ShareStatusToggle
                      settings={settings}
                      updateSettings={updateSettings}
                      disabled={!canEdit}
                    />

                    <PublicLinkSection
                      settings={settings}
                      shareUrl={shareUrl}
                    />

                    <AccessLevelSelector
                      settings={settings}
                      updateSettings={updateSettings}
                      disabled={!canEdit}
                    />
                  </>
                )}

                {activeTab === 'Workspace' && (
                  <>
                    <WorkspaceAccessSection
                      roles={roles}
                      workspaceSettings={workspaceSettings}
                      onUpdate={updateWorkspaceSettings}
                      disabled={!canEdit}
                    />

                    <WorkspaceLinkSection
                      workspaceAccess={workspaceRoleName}
                      shareUrl={shareUrl}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoShareModal;
